<template>
  <VaNavbar class="app-layout-navbar py-1 md:py-2 px-2 md:px-4">
    <template #left>
      <div class="left">
        <Transition v-if="isMobile" name="icon-fade" mode="out-in">
          <VaIcon
            color="primary"
            :name="isSidebarMinimized ? 'menu' : 'close'"
            size="20px"
            @click="isSidebarMinimized = !isSidebarMinimized"
          />
        </Transition>
        <RouterLink to="/" aria-label="Visit home page" class="app-brand">
          <img src="/logo.png" alt="HemoCare SRL" class="brand-image" />
          <span class="brand-title">HemoCare SRL</span>
        </RouterLink>
      </div>
    </template>
    <template #right>
      <AppNavbarActions class="app-navbar__actions" :is-mobile="isMobile" />
    </template>
  </VaNavbar>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
import AppNavbarActions from './components/AppNavbarActions.vue'

defineProps({
  isMobile: { type: Boolean, default: false },
})

const GlobalStore = useGlobalStore()

const { isSidebarMinimized } = storeToRefs(GlobalStore)
</script>

<style lang="scss" scoped>
.va-navbar {
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid var(--va-background-primary);
  align-items: center !important;
  height: 60px;
  padding-top: 0 !important;
  padding-bottom: 0 !important;

  @media screen and (max-width: 768px) {
    height: 56px;
  }

  @media screen and (max-width: 480px) {
    height: 52px;
  }

  @media screen and (max-width: 950px) {
    .left {
      width: 100%;
    }

    .app-navbar__actions {
      display: flex;
      justify-content: space-between;
    }
  }
}

.left {
  display: flex !important;
  align-items: center !important;
  margin-left: 0 !important;
  gap: 0.5rem;
  height: 100%;

  @media screen and (max-width: 768px) {
    margin-left: 0 !important;
    gap: 0.25rem;
  }

  & > * {
    margin-right: 0;
  }

  & > *:last-child {
    margin-right: 0;
  }
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: transform 0.5s ease;
}

.icon-fade-enter,
.icon-fade-leave-to {
  transform: scale(0.5);
}

.logo-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
}

.app-brand {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 1.5rem !important;
  text-decoration: none !important;
  cursor: pointer !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  padding: 0.5rem 1rem !important;
  border-radius: 0.5rem !important;
  margin: 0 !important;
  color: var(--va-text-primary) !important;

  @media screen and (max-width: 950px) {
    gap: 1rem !important;
    padding: 0.4rem 0.75rem !important;
  }

  @media screen and (max-width: 768px) {
    gap: 0.7rem !important;
    padding: 0.3rem 0.5rem !important;
  }

  @media screen and (max-width: 480px) {
    gap: 0.5rem !important;
    padding: 0.25rem 0.4rem !important;
  }

  @media screen and (max-width: 320px) {
    gap: 0.35rem !important;
    padding: 0.2rem 0.3rem !important;
  }

  &:hover {
    transform: translateY(-2px) !important;
    background-color: rgba(var(--va-primary-rgb, 15, 34, 204), 0.08) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }

  &:active {
    transform: translateY(0) !important;
  }
}

.app-brand .brand-title {
  display: inline-block !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 1.2rem !important;
  font-weight: 800 !important;
  color: var(--va-primary) !important;
  white-space: nowrap !important;
  letter-spacing: 0.5px !important;
  line-height: 1 !important;
  text-transform: uppercase !important;
  text-decoration: none !important;
  margin: 0 !important;
  padding: 0 !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media screen and (max-width: 950px) {
    font-size: 1.1rem !important;
  }

  @media screen and (max-width: 768px) {
    font-size: 0.95rem !important;
  }

  @media screen and (max-width: 640px) {
    font-size: 0.85rem !important;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.75rem !important;
    letter-spacing: 0.3px !important;
  }

  @media screen and (max-width: 360px) {
    font-size: 0.65rem !important;
  }
}

.brand-icon {
  color: var(--va-primary) !important;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.brand-image {
  height: 36px;
  width: auto;
  display: block;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;

  @media screen and (max-width: 950px) {
    height: 34px;
  }

  @media screen and (max-width: 768px) {
    height: 32px;
  }

  @media screen and (max-width: 480px) {
    height: 28px;
  }

  @media screen and (max-width: 360px) {
    height: 24px;
  }
}
</style>
